// One availability-query per date for the wizard slot-grid (delspår 3c; parity-fix 2026-07-16).
//
// Read-only. Previously this fetched only bookings(confirmed,blocked) — but the DB
// trigger that ultimately validates create_activity_v2's bookings rejects on FOUR
// sources (bookings incl. live-reserved, recurring_blocks, league_matches,
// recurring_bookings minus releases). The wizard therefore offered slots the create
// then refused. Now the grid asks get_unavailable_ranges — a SECURITY DEFINER
// function whose predicates are parity-tested against the trigger in its migration
// (20260716120000_get_unavailable_ranges) — so display == validation by construction.
// Overlap geometry is still computed client-side via wizardSlots; this hook only
// supplies the busy ranges.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { DayCourtBooking } from "@/lib/wizardSlots";

const EMPTY: DayCourtBooking[] = [];

interface UnavailableRange {
  court_id: string;
  busy_start: string;
  busy_end: string;
}

/** Fetch every busy range (all four blocking sources) for the courts on a date. */
export async function fetchUnavailableRanges(
  date: string,
  courtIds: string[],
): Promise<DayCourtBooking[]> {
  // Schema-ahead RPC (same pattern as create_activity_v2): generated types lag prod.
  const { data, error } = await (supabase.rpc as any)("get_unavailable_ranges", {
    p_court_ids: courtIds,
    p_date: date,
  });
  if (error) throw error;
  return ((data ?? []) as UnavailableRange[]).map((r) => ({
    court_id: r.court_id,
    start_time: r.busy_start,
    end_time: r.busy_end,
  }));
}

export function useDayCourtBookings(date: string | null | undefined, courtIds: string[]) {
  const sortedIds = [...courtIds].sort();
  const query = useQuery({
    queryKey: ["wizard-day-bookings", date, sortedIds],
    queryFn: () => fetchUnavailableRanges(date as string, sortedIds),
    enabled: !!date && sortedIds.length > 0,
  });

  return {
    bookings: query.data ?? EMPTY,
    isLoading: query.isLoading,
  };
}
